import {
  css,
  Heading,
  Link as SourcefulLink,
  PrimaryButton,
  styled,
} from "@sourceful/shared-components";
import { SanityPageSectionType, SanityTextAlign } from "@sourceful/shared-types";
import toRem from "@styles/postprocess/conversion/to-rem";
import { FC } from "react";
import { BlockContent } from "@lib/sanity-components";
import SmartLink from "@components/pageSectionAnchors/SmartLink";

export const PageSectionWrapper = styled("section", {
  width: "100%",
  //display: "grid",
  display: "flex",
  //justifyItems: "center",
  justifyContent: "center",
  minHeight: toRem(500),
  padding: "clamp(20px, 3vw, 60px)",
});

export const TextBlock = styled("div", {
  display: "grid",
  justifyItems: "center",
  gridGap: toRem(20),
  //paddingTop: toRem(30),
});

const CTAS = styled("div", {
  marginBlockStart: "$block_m1",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const ButtonGroup = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  justifyItems: "center",
  a: {
    margin: toRem(8),
  },
  "a:last-child": {
    marginRight: 0,
  },
});

const Content = styled("div", {
  width: "100%",
});

const blockContentCss = css({
  "& > div > *:last-child, & > *:last-child": {
    marginBottom: "0",
  },
});

function convertToJustifyItem(textAlign: SanityTextAlign | undefined) {
  switch (textAlign) {
    case "left":
      return "start";
    case "right":
      return "end";
    default:
      return "center";
  }
}

export const nestedOverrides = (nested: boolean): object => {
  if (nested === true)
    return {
      paddingInline: "0",
    };

  return {};
};

interface BasePageSectionProps {
  maxWidth?: string;
  minHeight?: string;
  className?: string;
  section: SanityPageSectionType;
  carousel?: boolean;
  children?: React.ReactNode;
}

export const BasePageSection: FC<BasePageSectionProps> = ({
  section,
  maxWidth = "1200px",
  children,
  className,
  minHeight,
}) => {
  const {
    title,
    subtitle,
    primaryCTA,
    secondaryCTA,
    linkCTA,
    showTitle,
    backgroundColour,
    textColour,
    constrainContent,
    textAlign,
    nested,
  } = section;

  //console.log("section", section);

  const renderCTAs = () => {
    if (!primaryCTA || !primaryCTA.url || !primaryCTA.title) {
      return null;
    }

    return (
      <CTAS>
        <ButtonGroup>
          <SmartLink href={primaryCTA.url}>
            <PrimaryButton as="a" tabIndex={0}>
              {primaryCTA.title}
            </PrimaryButton>
          </SmartLink>
          {secondaryCTA && secondaryCTA?.title && secondaryCTA?.url && (
            <SmartLink href={secondaryCTA.url}>
              <PrimaryButton as="a" appearance="light" tabIndex={0}>
                {secondaryCTA.title}
              </PrimaryButton>
            </SmartLink>
          )}
        </ButtonGroup>
        {linkCTA && linkCTA?.title && linkCTA?.url && (
          <SmartLink href={linkCTA.url}>
            <SourcefulLink href={linkCTA.url} css={{ marginTop: "20px" }}>
              {linkCTA.title}
            </SourcefulLink>
          </SmartLink>
        )}
      </CTAS>
    );
  };

  return (
    <PageSectionWrapper
      css={{
        backgroundColor: backgroundColour?.value || "auto",
        minHeight: minHeight || undefined,
        ...nestedOverrides(nested || false),
      }}
      className={className}
      id={`s${section._key}`}
      aria-labelledby={`sh${section._key}`}
    >
      <Content css={{ maxWidth: constrainContent ? maxWidth : "100%" }}>
        {title && showTitle && (
          <TextBlock
            className="section-text-block"
            css={{
              justifyItems: convertToJustifyItem(textAlign),
              textAlign: textAlign ?? "center",
              marginBlockEnd: "$block_m3",
            }}
          >
            {title && (
              <Heading
                level={2}
                fontSize={3}
                css={{ color: textColour?.value || "$neutral_solid_100", textAlign: "center" }}
                id={`sh${section._key}`}
              >
                {title}
              </Heading>
            )}
            {subtitle && (
              <BlockContent
                wrapperClassName={blockContentCss()}
                value={subtitle}
                color={textColour?.value}
              />
            )}
          </TextBlock>
        )}
        {children}
        {renderCTAs()}
      </Content>
    </PageSectionWrapper>
  );
};

export default BasePageSection;
